import { forwardRef, type MouseEventHandler, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/common'

import { Spinner } from './spinner'

const BORDER_BASE = 'border border-solid border-gray-light-300'

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-1 font-medium focus:outline-none transition-colors cursor-pointer px-[14px] py-[10px] rounded-lg disabled:cursor-not-allowed disabled:bg-disabled disabled:text-foreground-disabled disabled:border-disabled-border disabled:hover:bg-disabled disabled:hover:text-foreground-disabled disabled:active:bg-disabled',
  {
    variants: {
      variant: {
        solid: 'border-none',
        outlined: 'bg-transparent border',
        text: 'bg-transparent border-none',
        link: 'bg-transparent border-none underline p-0',
        default: `border border-gray-300 bg-white`,
      },
      color: {
        primary: '',
        black: '',
        default: '',
      },
      rounded: {
        true: 'rounded-full',
        false: 'rounded-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      // Solid variants
      {
        variant: 'solid',
        color: 'primary',
        class:
          'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus:ring-2 focus:ring-blue-300',
      },
      {
        variant: 'solid',
        color: 'black',
        class:
          'bg-black text-white hover:bg-neutral-900 active:bg-neutral-950 focus:ring-2 focus:ring-neutral-300',
      },
      {
        variant: 'solid',
        color: 'default',
        class: `bg-gray-200 ${BORDER_BASE} text-gray-900 hover:bg-gray-300 active:bg-gray-400 focus:ring-2 focus:ring-gray-200`,
      },
      // Outlined variants
      {
        variant: 'outlined',
        color: 'primary',
        class: `border-blue-600 ${BORDER_BASE} text-blue-600 hover:bg-blue-50 active:bg-blue-100 focus:ring-2 focus:ring-blue-100`,
      },
      {
        variant: 'outlined',
        color: 'black',
        class: `border-neutral-900 ${BORDER_BASE} text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100 focus:ring-2 focus:ring-neutral-100`,
      },
      {
        variant: 'outlined',
        color: 'default',
        class: `border-gray-400 ${BORDER_BASE} text-gray-900 hover:bg-gray-100 active:bg-gray-200 focus:ring-2 focus:ring-gray-100`,
      },
      // Text variants
      {
        variant: 'text',
        color: 'primary',
        class: 'text-blue-600 hover:bg-blue-50 active:bg-blue-100',
      },
      {
        variant: 'text',
        color: 'black',
        class: 'text-neutral-900',
      },
      {
        variant: 'text',
        color: 'default',
        class: `${BORDER_BASE} text-gray-900`,
      },
      // Link variants
      {
        variant: 'link',
        color: 'primary',
        class: 'text-blue-600 hover:text-blue-800 active:text-blue-900',
      },
      {
        variant: 'link',
        color: 'black',
        class: 'text-neutral-900 hover:text-black active:text-black',
      },
      {
        variant: 'link',
        color: 'default',
        class: 'text-gray-900 hover:text-gray-700 active:text-gray-800',
      },
      // Default variants
      {
        variant: 'default',
        color: 'default',
        class: `${BORDER_BASE} text-gray-900 hover:bg-gray-100 active:bg-gray-200 focus:ring-2 focus:ring-gray-200`,
      },
      {
        variant: 'default',
        color: 'primary',
        class: `${BORDER_BASE} text-blue-600 hover:bg-blue-50 active:bg-blue-100 focus:ring-2 focus:ring-blue-200`,
      },
      {
        variant: 'default',
        color: 'black',
        class: `${BORDER_BASE} text-neutral-900 hover:bg-neutral-100 active:bg-neutral-200 focus:ring-2 focus:ring-neutral-200`,
      },
    ],
    defaultVariants: {
      variant: 'solid',
      color: 'primary',
      rounded: false,
      fullWidth: false,
    },
  }
)

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  loading?: boolean
  loadingIcon?: ReactNode
}

/**
 * Button Component
 *
 * A flexible button component built on shadcn/ui with custom variants and states.
 * Leverages class-variance-authority (CVA) for type-safe variant composition.
 * Supports multiple variants (solid, outlined, text, link), colors, sizes, loading states, and icons.
 *
 * Key features:
 * - Built on shadcn/ui Button for accessibility and composition
 * - Type-safe variants using CVA
 * - Multiple visual variants: solid, outlined, text, link, default
 * - Three color options: primary (blue), black, default (gray)
 * - Loading state with spinner
 * - Leading and trailing icon support
 * - Rounded or default border radius
 * - Full width option
 * - Disabled state handling
 *
 * @param variant - Visual style variant (solid, outlined, text, link, default)
 * @param color - Color scheme (primary, black, default)
 * @param rounded - Use rounded-full instead of rounded-lg
 * @param leadingIcon - Icon to display before the button text
 * @param trailingIcon - Icon to display after the button text
 * @param loading - Show loading spinner and disable interaction
 * @param loadingIcon - Custom loading spinner icon
 * @param fullWidth - Make button take full width of container
 * @param disabled - Disable button interaction
 * @param children - Button text or content
 * @param className - Additional CSS classes
 *
 * @example
 * ```tsx
 * <Button variant="solid" color="primary">
 *   Click me
 * </Button>
 *
 * <Button variant="outlined" leadingIcon={<Icon />} loading>
 *   Loading...
 * </Button>
 * ```
 */
// Helper component for icon wrappers to reduce duplication
const IconWrapper = ({ children }: { children: ReactNode }) => (
  <div className="inline-flex items-center">{children}</div>
)

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      disabled = false,
      rounded = false,
      variant = 'solid',
      leadingIcon,
      trailingIcon,
      loading = false,
      loadingIcon,
      color = 'primary',
      children,
      type = 'button',
      onClick,
      fullWidth = false,
      asChild = false,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
      if (isDisabled) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      onClick?.(event)
      event.currentTarget.blur()
    }

    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        type={type}
        className={cn(
          buttonVariants({ variant, color, rounded, fullWidth }),
          className
        )}
        onClick={handleClick}
        disabled={isDisabled}
        aria-busy={loading}
        {...rest}
      >
        {leadingIcon && !loading && <IconWrapper>{leadingIcon}</IconWrapper>}
        <span className="inline-flex items-center justify-center leading-none">
          {children}
        </span>
        {loading ? (
          <IconWrapper>
            {loadingIcon ? (
              <span className="animate-spin">{loadingIcon}</span>
            ) : (
              <Spinner />
            )}
          </IconWrapper>
        ) : (
          trailingIcon && <IconWrapper>{trailingIcon}</IconWrapper>
        )}
      </Comp>
    )
  }
)

Button.displayName = 'Button'
